<template>
  <AuthContainer>
    <div class="unsubscribe-container">
      <TitleContainer>
        <template #default>
          <h2 v-if="!loading" class="title">
            {{
              isUnsubscribed ? 'You are already unsubscribed!' : 'Unsubscribe'
            }}
          </h2>
        </template>
      </TitleContainer>
      <LoadingBlock :loading="loading" />

      <template v-if="!loading">
        <p
          v-if="errors.message || isUnsubscribed === null"
          class="error-message"
        >
          {{ errors.message || 'Invalid unsubscribe link' }}
        </p>

        <div v-else>
          <p>
            {{
              isUnsubscribed
                ? "You won't receive our regular notifications any more."
                : 'Are you sure you want to unsubscribe?'
            }}
          </p>
          <div v-show="!isUnsubscribed" class="button-wrapper">
            <el-button
              type="primary"
              size="large"
              class="action-button"
              @click.prevent="onSubmit"
            >
              Unsubscribe
            </el-button>
            <el-button
              type="default"
              size="large"
              class="action-button"
              @click.prevent="goHome"
            >
              Back
            </el-button>
          </div>
        </div>
      </template>
    </div>
  </AuthContainer>
</template>

<script lang="ts">
import { ref, defineComponent, onMounted, computed } from 'vue'
import TitleContainer from '@/components/Auth/TitleContainer.vue'
import AuthContainer from '@/components/Auth/AuthContainer.vue'
import useValidation from '@/hooks/useValidation'
import LoadingBlock from '@/components/LoadingBlock.vue'
import { ElMessage } from 'element-plus'

import { UnsubscribeValidationErrorsKey } from '@/api/types'
import { safeSubmitProcessing } from '@/utils/helpers'
import { useUserStore } from '@/store/modules/user'
import useRouter from '@/hooks/useRouter'

export default defineComponent({
  name: 'Unsubscribe',
  components: {
    TitleContainer,
    AuthContainer,
    LoadingBlock
  },
  setup() {
    const loading = ref(false)
    const isUnsubscribed = computed(() => userStore.unsubscribed)

    const { provideValidation, setValidationErrors } = useValidation(
      UnsubscribeValidationErrorsKey
    )
    const errors = provideValidation()
    const { router, route } = useRouter()
    const userStore = useUserStore()

    const token = route.value.params.token

    const onSuccess = async () => {
      errors.value = {}
      ElMessage({
        type: 'success',
        message: 'You have successfully unsubscribed.',
        showClose: true,
        duration: 5000
      })
    }

    const onSubmit = async () => {
      await safeSubmitProcessing(
        loading,
        () => userStore.Unsubscribe(token),
        () => onSuccess(),
        (err) => {
          setValidationErrors(err, errors)
          ElMessage.error(errors.value.message)
        }
      )
    }

    const goHome = () => {
      router.push('/')
    }

    onMounted(() => {
      loading.value = true

      userStore
        .CheckSubscription(token)
        .catch((err) => {
          setValidationErrors(err, errors)
        })
        .finally(() => {
          loading.value = false
        })
    })

    return {
      errors,
      loading,
      isUnsubscribed,
      onSubmit,
      goHome
    }
  }
})
</script>
<style lang="scss" scoped>
.error-message {
  background-color: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fca5a5;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
}
.unsubscribe-container {
  padding: 20px 20px;
  background: #f5f5f5;
  border-radius: 5px;
  .loading-container {
    :deep(.el-loading-mask) {
      background-color: #f5f5f5;
    }
  }
}
.button-wrapper {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;

  .action-button {
    width: 200px;
  }
}
</style>
